"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { getSetSummaries } from "../lib/opIndex";

type ToggleState = "include" | "exclude" | "ignore";

const PAGE_ID_PREFIX = "advanced";

const TOGGLE_ON = "rounded-full bg-blue-600 px-3 py-1 text-xs";
const TOGGLE_OFF = "rounded-full bg-slate-800 px-3 py-1 text-xs";

export default function AdvancedSearchPage() {
  const [selectedSets, setSelectedSets] = useState<string[]>([]);
  const [setFilterText, setSetFilterText] = useState<string>("");
  const [isSetDropdownOpen, setIsSetDropdownOpen] = useState<boolean>(false);
  const [nameContains, setNameContains] = useState<string>("");
  const [textContains, setTextContains] = useState<string>("");
  const [cardId, setCardId] = useState<string>("");
  const [leader, setLeader] = useState<boolean>(false);
  const [character, setCharacter] = useState<boolean>(false);
  const [event, setEvent] = useState<boolean>(false);
  const [stage, setStage] = useState<boolean>(false);
  const [don, setDon] = useState<boolean>(false);

  const [red, setRed] = useState<boolean>(false);
  const [blue, setBlue] = useState<boolean>(false);
  const [green, setGreen] = useState<boolean>(false);
  const [black, setBlack] = useState<boolean>(false);
  const [yellow, setYellow] = useState<boolean>(false);
  const [purple, setPurple] = useState<boolean>(false);

  const [costMin, setCostMin] = useState<string>("");
  const [costMax, setCostMax] = useState<string>("");
  const [powMin, setPowMin] = useState<string>("");
  const [powMax, setPowMax] = useState<string>("");

  const [featureText, setFeatureText] = useState<string>("");

  const [triggerState, setTriggerState] = useState<ToggleState>("ignore");
  const [counterState, setCounterState] = useState<ToggleState>("ignore");

  const [rarityC, setRarityC] = useState<boolean>(false);
  const [rarityUC, setRarityUC] = useState<boolean>(false);
  const [rarityR, setRarityR] = useState<boolean>(false);
  const [raritySR, setRaritySR] = useState<boolean>(false);
  const [raritySEC, setRaritySEC] = useState<boolean>(false);
  const [rarityL, setRarityL] = useState<boolean>(false);
  const [rarityTR, setRarityTR] = useState<boolean>(false);
  const [raritySP, setRaritySP] = useState<boolean>(false);
  const [rarityP, setRarityP] = useState<boolean>(false);

  const [artAlternate, setArtAlternate] = useState<boolean>(false);
  const [artManga, setArtManga] = useState<boolean>(false);
  const [artUnique, setArtUnique] = useState<boolean>(false);

  const [kwRush, setKwRush] = useState<boolean>(false);
  const [kwBlocker, setKwBlocker] = useState<boolean>(false);

  const [attrStrike, setAttrStrike] = useState<boolean>(false);
  const [attrRanged, setAttrRanged] = useState<boolean>(false);
  const [attrSlash, setAttrSlash] = useState<boolean>(false);
  const [attrWisdom, setAttrWisdom] = useState<boolean>(false);
  const [attrSpecial, setAttrSpecial] = useState<boolean>(false);

  const [blockIconMin, setBlockIconMin] = useState<string>("");
  const [blockIconMax, setBlockIconMax] = useState<string>("");

  const summaries = useMemo(() => getSetSummaries(), []);

  const buildQuery = (): string => {
    const parts: string[] = [];

    if (selectedSets.length === 1) {
      parts.push(`set:${selectedSets[0]}`);
    } else if (selectedSets.length > 1) {
      const sets = selectedSets.map((c) => `set:${c}`);
      parts.push(`(${sets.join(" or ")})`);
    }

    if (cardId.trim()) {
      parts.push(`id:${cardId.trim()}`);
    }

    const types: string[] = [];
    if (leader) types.push("t:leader");
    if (character) types.push("t:character");
    if (event) types.push("t:event");
    if (stage) types.push("t:stage");
    if (don) types.push("t:DON!!");
    if (types.length === 1) {
      parts.push(types[0]);
    } else if (types.length > 1) {
      parts.push(`(${types.join(" or ")})`);
    }

    const colors: string[] = [];
    if (red) colors.push("c:red");
    if (blue) colors.push("c:blue");
    if (green) colors.push("c:green");
    if (black) colors.push("c:black");
    if (yellow) colors.push("c:yellow");
    if (purple) colors.push("c:purple");
    parts.push(...colors);

    const rarities: string[] = [];
    if (rarityC) rarities.push("r:c");
    if (rarityUC) rarities.push("r:uc");
    if (rarityR) rarities.push("r:r");
    if (raritySR) rarities.push("r:sr");
    if (raritySEC) rarities.push("r:sec");
    if (rarityL) rarities.push("r:l");
    if (rarityTR) rarities.push("r:tr");
    if (raritySP) rarities.push("r:sp");
    if (rarityP) rarities.push("r:p");
    if (rarities.length === 1) {
      parts.push(rarities[0]);
    } else if (rarities.length > 1) {
      parts.push(`(${rarities.join(" or ")})`);
    }

    if (costMin.trim()) parts.push(`cost>=${costMin.trim()}`);
    if (costMax.trim()) parts.push(`cost<=${costMax.trim()}`);
    if (powMin.trim()) parts.push(`pow>=${powMin.trim()}`);
    if (powMax.trim()) parts.push(`pow<=${powMax.trim()}`);

    if (blockIconMin.trim()) parts.push(`bi>=${blockIconMin.trim()}`);
    if (blockIconMax.trim()) parts.push(`bi<=${blockIconMax.trim()}`);

    if (featureText.trim()) {
      parts.push(`feature:"${featureText.trim()}"`);
    }

    if (triggerState === "include") parts.push("has:trigger");
    if (triggerState === "exclude") parts.push("-has:trigger");

    if (counterState === "include") parts.push("has:counter");
    if (counterState === "exclude") parts.push("-has:counter");

    const arts: string[] = [];
    if (artAlternate) arts.push("art:alternate");
    if (artManga) arts.push("art:manga");
    if (artUnique) arts.push("art:unique");
    if (arts.length === 1) {
      parts.push(arts[0]);
    } else if (arts.length > 1) {
      parts.push(`(${arts.join(" or ")})`);
    }

    if (kwRush) parts.push("k:rush");
    if (kwBlocker) parts.push("k:blocker");

    const attrs: string[] = [];
    if (attrStrike) attrs.push("a:strike");
    if (attrRanged) attrs.push("a:ranged");
    if (attrSlash) attrs.push("a:slash");
    if (attrWisdom) attrs.push("a:wisdom");
    if (attrSpecial) attrs.push("a:special");
    if (attrs.length === 1) {
      parts.push(attrs[0]);
    } else if (attrs.length > 1) {
      parts.push(`(${attrs.join(" or ")})`);
    }

    if (nameContains.trim()) {
      parts.push(`name:"${nameContains.trim()}"`);
    }
    if (textContains.trim()) {
      parts.push(`o:"${textContains.trim()}"`);
    }

    return parts.join(" ");
  };

  const query = buildQuery();

  const handleSearch = () => {
    const q = query.trim();
    const url = new URL(window.location.href);
    url.pathname = "/search";
    if (q) {
      url.search = `?q=${encodeURIComponent(q)}`;
    } else {
      url.search = "";
    }
    window.location.href = url.toString();
  };

  return (
    <div
      id={`${PAGE_ID_PREFIX}-page`}
      className="flex min-h-screen flex-col bg-slate-950 text-slate-50"
    >
      <Header idPrefix={`${PAGE_ID_PREFIX}-header`} />

      <main
        id={`${PAGE_ID_PREFIX}-main`}
        className="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-4 px-4 py-5"
      >
        <section
          id={`${PAGE_ID_PREFIX}-builder-section`}
          className="w-full space-y-4"
        >
          <div className="space-y-3 rounded-xl border border-slate-800 bg-slate-900/70 p-4 text-sm">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h2
                  id={`${PAGE_ID_PREFIX}-builder-title`}
                  className="text-sm font-semibold text-slate-100"
                >
                  Build a query
                </h2>
                <p className="text-xs text-slate-400">
                  Use filters below to construct a Punk Records search query.
                </p>
              </div>
              <button
                id={`${PAGE_ID_PREFIX}-builder-search-button`}
                type="button"
                className="rounded-full bg-blue-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-500"
                onClick={handleSearch}
              >
                Search
              </button>
            </div>

            <div className="space-y-3">
              {/* Set code */}
              <label className="block text-xs text-slate-300">
                <span className="mb-1 block font-medium">Set code</span>
                <div
                  id={`${PAGE_ID_PREFIX}-set-code-input`}
                  className="relative"
                >
                  <button
                    type="button"
                    className="flex w-full flex-wrap items-center gap-1 rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
                    onClick={() => setIsSetDropdownOpen((open) => !open)}
                  >
                    {selectedSets.length === 0 && (
                      <span className="text-slate-500">
                        Select sets (e.g. OP01, EB01…)
                      </span>
                    )}
                    {selectedSets.map((code) => {
                      const summary = summaries.find((s) => s.code === code);
                      const label = summary ? `${summary.name} (${code})` : code;
                      return (
                        <span
                          key={code}
                          className="inline-flex items-center gap-1 rounded-full bg-slate-800 px-2 py-0.5"
                        >
                          <button
                            type="button"
                            className="text-slate-400 hover:text-slate-200"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedSets((prev) =>
                                prev.filter((c) => c !== code),
                              );
                            }}
                            aria-label={`Remove ${code}`}
                          >
                            ×
                          </button>
                          <span className="whitespace-nowrap">{label}</span>
                        </span>
                      );
                    })}
                    <input
                      type="text"
                      value={setFilterText}
                      onChange={(e) => {
                        setSetFilterText(e.target.value.toUpperCase());
                        if (!isSetDropdownOpen) {
                          setIsSetDropdownOpen(true);
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsSetDropdownOpen(true);
                      }}
                      placeholder={
                        selectedSets.length ? "Add another set…" : ""
                      }
                      className="flex-1 min-w-[4rem] bg-transparent px-1 py-0.5 text-sm text-slate-100 outline-none"
                    />
                  </button>
                  {isSetDropdownOpen && (
                    <div className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-md border border-slate-800 bg-slate-950 text-xs shadow-lg">
                      {summaries
                        .filter((s) => {
                          const term = setFilterText.trim();
                          if (!term) return true;
                          const code = s.code.toUpperCase();
                          const name = (s.name || "").toUpperCase();
                          return (
                            code.includes(term) || name.includes(term)
                          );
                        })
                        .map((s) => {
                          const checked = selectedSets.includes(s.code);
                          return (
                            <button
                              key={s.code}
                              type="button"
                              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-slate-900 ${
                                checked ? "bg-slate-900/70" : ""
                              }`}
                              onClick={() => {
                                setSelectedSets((prev) =>
                                  prev.includes(s.code)
                                    ? prev.filter((c) => c !== s.code)
                                    : [...prev, s.code],
                                );
                              }}
                            >
                              <span
                                className={`flex h-3 w-3 items-center justify-center rounded border ${
                                  checked
                                    ? "border-blue-500 bg-blue-500"
                                    : "border-slate-600 bg-slate-900"
                                }`}
                              >
                                {checked && (
                                  <span className="h-2 w-2 rounded-sm bg-slate-950" />
                                )}
                              </span>
                              <span className="font-mono text-slate-100">
                                {s.code}
                              </span>
                              <span className="text-slate-400">{s.name}</span>
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>
              </label>

              {/* Card ID */}
              <label
                className="block text-xs text-slate-300"
                htmlFor={`${PAGE_ID_PREFIX}-card-id-input`}
              >
                <span className="mb-1 block font-medium">Card ID / number</span>
                <input
                  id={`${PAGE_ID_PREFIX}-card-id-input`}
                  type="text"
                  value={cardId}
                  onChange={(e) => setCardId(e.target.value)}
                  placeholder="OP01-001, ST07-015..."
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
                />
              </label>

              {/* Card types */}
              <div className="space-y-1 text-xs text-slate-300">
                <div className="font-medium">Card types</div>
                <div className="flex flex-wrap gap-1.5">
                  <button id={`${PAGE_ID_PREFIX}-type-toggle-leader`} type="button" className={leader ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setLeader((v) => !v)}>Leader</button>
                  <button id={`${PAGE_ID_PREFIX}-type-toggle-character`} type="button" className={character ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setCharacter((v) => !v)}>Character</button>
                  <button id={`${PAGE_ID_PREFIX}-type-toggle-event`} type="button" className={event ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setEvent((v) => !v)}>Event</button>
                  <button id={`${PAGE_ID_PREFIX}-type-toggle-stage`} type="button" className={stage ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setStage((v) => !v)}>Stage</button>
                  <button id={`${PAGE_ID_PREFIX}-type-toggle-don`} type="button" className={don ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setDon((v) => !v)}>DON!!</button>
                </div>
              </div>

              {/* Colors */}
              <div className="space-y-1 text-xs text-slate-300">
                <div className="font-medium">Colors</div>
                <div className="flex flex-wrap gap-1.5">
                  <button id={`${PAGE_ID_PREFIX}-color-toggle-red`} type="button" className={red ? "rounded-full bg-red-600 px-3 py-1 text-xs" : TOGGLE_OFF} onClick={() => setRed((v) => !v)}>Red</button>
                  <button id={`${PAGE_ID_PREFIX}-color-toggle-blue`} type="button" className={blue ? "rounded-full bg-sky-600 px-3 py-1 text-xs" : TOGGLE_OFF} onClick={() => setBlue((v) => !v)}>Blue</button>
                  <button id={`${PAGE_ID_PREFIX}-color-toggle-green`} type="button" className={green ? "rounded-full bg-emerald-600 px-3 py-1 text-xs" : TOGGLE_OFF} onClick={() => setGreen((v) => !v)}>Green</button>
                  <button id={`${PAGE_ID_PREFIX}-color-toggle-black`} type="button" className={black ? "rounded-full bg-slate-700 px-3 py-1 text-xs" : TOGGLE_OFF} onClick={() => setBlack((v) => !v)}>Black</button>
                  <button id={`${PAGE_ID_PREFIX}-color-toggle-yellow`} type="button" className={yellow ? "rounded-full bg-yellow-500 px-3 py-1 text-xs text-slate-900" : TOGGLE_OFF} onClick={() => setYellow((v) => !v)}>Yellow</button>
                  <button id={`${PAGE_ID_PREFIX}-color-toggle-purple`} type="button" className={purple ? "rounded-full bg-purple-600 px-3 py-1 text-xs" : TOGGLE_OFF} onClick={() => setPurple((v) => !v)}>Purple</button>
                </div>
              </div>

              {/* Rarity */}
              <div className="space-y-1 text-xs text-slate-300">
                <div className="font-medium">Rarity</div>
                <div className="flex flex-wrap gap-1.5">
                  <button id={`${PAGE_ID_PREFIX}-rarity-toggle-c`} type="button" className={rarityC ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setRarityC((v) => !v)}>C</button>
                  <button id={`${PAGE_ID_PREFIX}-rarity-toggle-uc`} type="button" className={rarityUC ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setRarityUC((v) => !v)}>UC</button>
                  <button id={`${PAGE_ID_PREFIX}-rarity-toggle-r`} type="button" className={rarityR ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setRarityR((v) => !v)}>R</button>
                  <button id={`${PAGE_ID_PREFIX}-rarity-toggle-sr`} type="button" className={raritySR ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setRaritySR((v) => !v)}>SR</button>
                  <button id={`${PAGE_ID_PREFIX}-rarity-toggle-sec`} type="button" className={raritySEC ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setRaritySEC((v) => !v)}>SEC</button>
                  <button id={`${PAGE_ID_PREFIX}-rarity-toggle-l`} type="button" className={rarityL ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setRarityL((v) => !v)}>L</button>
                  <button id={`${PAGE_ID_PREFIX}-rarity-toggle-tr`} type="button" className={rarityTR ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setRarityTR((v) => !v)}>TR</button>
                  <button id={`${PAGE_ID_PREFIX}-rarity-toggle-sp`} type="button" className={raritySP ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setRaritySP((v) => !v)}>SP</button>
                  <button id={`${PAGE_ID_PREFIX}-rarity-toggle-p`} type="button" className={rarityP ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setRarityP((v) => !v)}>P (Promo)</button>
                </div>
              </div>

              {/* Cost & Power ranges */}
              <div className="flex flex-wrap gap-3 text-xs text-slate-300">
                <label
                  className="flex flex-1 flex-col gap-1"
                  htmlFor={`${PAGE_ID_PREFIX}-cost-min-input`}
                >
                  <span className="font-medium">Cost between</span>
                  <div className="flex items-center gap-2">
                    <input
                      id={`${PAGE_ID_PREFIX}-cost-min-input`}
                      type="number"
                      min={0}
                      value={costMin}
                      onChange={(e) => setCostMin(e.target.value)}
                      className="w-20 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40"
                    />
                    <span className="text-slate-400">and</span>
                    <input
                      id={`${PAGE_ID_PREFIX}-cost-max-input`}
                      type="number"
                      min={0}
                      value={costMax}
                      onChange={(e) => setCostMax(e.target.value)}
                      className="w-20 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40"
                    />
                  </div>
                </label>
                <label
                  className="flex flex-1 flex-col gap-1"
                  htmlFor={`${PAGE_ID_PREFIX}-pow-min-input`}
                >
                  <span className="font-medium">Power between</span>
                  <div className="flex items-center gap-2">
                    <input
                      id={`${PAGE_ID_PREFIX}-pow-min-input`}
                      type="number"
                      min={0}
                      step={1000}
                      value={powMin}
                      onChange={(e) => setPowMin(e.target.value)}
                      className="w-24 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40"
                    />
                    <span className="text-slate-400">and</span>
                    <input
                      id={`${PAGE_ID_PREFIX}-pow-max-input`}
                      type="number"
                      min={0}
                      step={1000}
                      value={powMax}
                      onChange={(e) => setPowMax(e.target.value)}
                      className="w-24 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40"
                    />
                  </div>
                </label>
              </div>

              {/* Block icon range */}
              <label
                className="flex flex-col gap-1 text-xs text-slate-300"
                htmlFor={`${PAGE_ID_PREFIX}-block-icon-min-input`}
              >
                <span className="font-medium">Block icon between</span>
                <div className="flex items-center gap-2">
                  <input
                    id={`${PAGE_ID_PREFIX}-block-icon-min-input`}
                    type="number"
                    min={0}
                    value={blockIconMin}
                    onChange={(e) => setBlockIconMin(e.target.value)}
                    className="w-20 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40"
                  />
                  <span className="text-slate-400">and</span>
                  <input
                    id={`${PAGE_ID_PREFIX}-block-icon-max-input`}
                    type="number"
                    min={0}
                    value={blockIconMax}
                    onChange={(e) => setBlockIconMax(e.target.value)}
                    className="w-20 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/40"
                  />
                </div>
              </label>

              {/* Feature */}
              <label
                className="block text-xs text-slate-300"
                htmlFor={`${PAGE_ID_PREFIX}-feature-input`}
              >
                <span className="mb-1 block font-medium">Feature contains</span>
                <input
                  id={`${PAGE_ID_PREFIX}-feature-input`}
                  type="text"
                  value={featureText}
                  onChange={(e) => setFeatureText(e.target.value)}
                  placeholder="Straw Hat Crew, Navy..."
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
                />
              </label>

              {/* Attributes */}
              <div className="space-y-1 text-xs text-slate-300">
                <div className="font-medium">Attributes</div>
                <div className="flex flex-wrap gap-1.5">
                  <button id={`${PAGE_ID_PREFIX}-attr-toggle-strike`} type="button" className={attrStrike ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setAttrStrike((v) => !v)}>Strike</button>
                  <button id={`${PAGE_ID_PREFIX}-attr-toggle-ranged`} type="button" className={attrRanged ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setAttrRanged((v) => !v)}>Ranged</button>
                  <button id={`${PAGE_ID_PREFIX}-attr-toggle-slash`} type="button" className={attrSlash ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setAttrSlash((v) => !v)}>Slash</button>
                  <button id={`${PAGE_ID_PREFIX}-attr-toggle-wisdom`} type="button" className={attrWisdom ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setAttrWisdom((v) => !v)}>Wisdom</button>
                  <button id={`${PAGE_ID_PREFIX}-attr-toggle-special`} type="button" className={attrSpecial ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setAttrSpecial((v) => !v)}>Special</button>
                </div>
              </div>

              {/* Trigger & Counter */}
              <div className="flex flex-wrap gap-4 text-xs text-slate-300">
                <div className="space-y-1">
                  <div className="font-medium">Trigger</div>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      id={`${PAGE_ID_PREFIX}-trigger-toggle-include`}
                      type="button"
                      className={triggerState === "include" ? TOGGLE_ON : TOGGLE_OFF}
                      onClick={() => setTriggerState((s) => s === "include" ? "ignore" : "include")}
                    >
                      Has trigger
                    </button>
                    <button
                      id={`${PAGE_ID_PREFIX}-trigger-toggle-exclude`}
                      type="button"
                      className={triggerState === "exclude" ? TOGGLE_ON : TOGGLE_OFF}
                      onClick={() => setTriggerState((s) => s === "exclude" ? "ignore" : "exclude")}
                    >
                      No trigger
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="font-medium">Counter</div>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      id={`${PAGE_ID_PREFIX}-counter-toggle-include`}
                      type="button"
                      className={counterState === "include" ? TOGGLE_ON : TOGGLE_OFF}
                      onClick={() => setCounterState((s) => s === "include" ? "ignore" : "include")}
                    >
                      Has counter
                    </button>
                    <button
                      id={`${PAGE_ID_PREFIX}-counter-toggle-exclude`}
                      type="button"
                      className={counterState === "exclude" ? TOGGLE_ON : TOGGLE_OFF}
                      onClick={() => setCounterState((s) => s === "exclude" ? "ignore" : "exclude")}
                    >
                      No counter
                    </button>
                  </div>
                </div>
              </div>

              {/* Art variants */}
              <div className="space-y-1 text-xs text-slate-300">
                <div className="font-medium">Art variants</div>
                <div className="flex flex-wrap gap-1.5">
                  <button id={`${PAGE_ID_PREFIX}-art-toggle-alternate`} type="button" className={artAlternate ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setArtAlternate((v) => !v)}>Alternate</button>
                  <button id={`${PAGE_ID_PREFIX}-art-toggle-manga`} type="button" className={artManga ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setArtManga((v) => !v)}>Manga</button>
                  <button id={`${PAGE_ID_PREFIX}-art-toggle-unique`} type="button" className={artUnique ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setArtUnique((v) => !v)}>Unique</button>
                </div>
              </div>

              {/* Keywords */}
              <div className="space-y-1 text-xs text-slate-300">
                <div className="font-medium">Keywords</div>
                <div className="flex flex-wrap gap-1.5">
                  <button id={`${PAGE_ID_PREFIX}-kw-toggle-rush`} type="button" className={kwRush ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setKwRush((v) => !v)}>Rush</button>
                  <button id={`${PAGE_ID_PREFIX}-kw-toggle-blocker`} type="button" className={kwBlocker ? TOGGLE_ON : TOGGLE_OFF} onClick={() => setKwBlocker((v) => !v)}>Blocker</button>
                </div>
              </div>

              {/* Name & Text */}
              <label
                className="block text-xs text-slate-300"
                htmlFor={`${PAGE_ID_PREFIX}-name-input`}
              >
                <span className="mb-1 block font-medium">Name contains</span>
                <input
                  id={`${PAGE_ID_PREFIX}-name-input`}
                  type="text"
                  value={nameContains}
                  onChange={(e) => setNameContains(e.target.value)}
                  placeholder="Luffy, Zoro..."
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
                />
              </label>

              <label
                className="block text-xs text-slate-300"
                htmlFor={`${PAGE_ID_PREFIX}-text-contains-input`}
              >
                <span className="mb-1 block font-medium">Text contains</span>
                <input
                  id={`${PAGE_ID_PREFIX}-text-contains-input`}
                  type="text"
                  value={textContains}
                  onChange={(e) => setTextContains(e.target.value)}
                  placeholder="Blocker, Rush..."
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
                />
              </label>

              {/* Generated query */}
              <div className="space-y-1 text-xs text-slate-300">
                <div className="font-medium">Generated query</div>
                <textarea
                  id={`${PAGE_ID_PREFIX}-generated-query`}
                  readOnly
                  value={query}
                  rows={3}
                  className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-100 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/40"
                />
              </div>

              <div className="flex flex-wrap gap-2 text-xs">
                <button
                  id={`${PAGE_ID_PREFIX}-run-search-button`}
                  type="button"
                  className="rounded-full bg-blue-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-blue-500"
                  onClick={handleSearch}
                >
                  Search
                </button>
                <button
                  id={`${PAGE_ID_PREFIX}-copy-query-button`}
                  type="button"
                  className="rounded-full bg-slate-800 px-4 py-1.5 text-xs font-medium text-slate-100 hover:bg-slate-700"
                  onClick={() => {
                    if (!query.trim()) return;
                    if (navigator.clipboard) {
                      navigator.clipboard.writeText(query).catch(() => {
                        // ignore
                      });
                    }
                  }}
                >
                  Copy query
                </button>
              </div>

              <p className="mt-2 text-xs text-slate-400">
                Advanced search builds a Punk Records query and forwards you to{" "}
                <code className="rounded bg-slate-800 px-1 py-0.5 text-xs">
                  /search
                </code>
                . For full syntax details, see the{" "}
                <Link
                  id={`${PAGE_ID_PREFIX}-syntax-link`}
                  href="/syntax"
                  className="text-blue-400 hover:underline"
                >
                  syntax page
                </Link>
                .
              </p>
            </div>
          </div>
        </section>

      </main>

      <Footer idPrefix={`${PAGE_ID_PREFIX}-footer`} />
    </div>
  );
}
